import * as THREE from 'three';
import IProduct from '../interfaces/IProduct';
import ProductGeometry from './ProductGeometry'
import Product from './Product';
import IProductGeometry from './IProductGeometry';
import { IMaterialSet } from '../services/MaterialManager';
import MaterialPair from './material/MaterialPair';
import { createProductRepresentation } from './bimProduct/ProductRepresentationFactory';
import ProductRepresentation from './bimProduct/ProductRepresentation';
import Representation2d from './representation2d/Representation2d';
import Representation3d from './representation3d/Representation3d';
import Representation3dTypeEnum from './representation3d/Representation3dTypeEnum';
import { ElementTypeEnum } from '../elementTypeMappings';
import { build } from 'vite';
import { buildProductName } from '../utils/productNameUtils';
import { StageTypeEnum } from '../store/viewerState/ViewerMode/ViewerMode';
import { ProductMeshUserData, VisualizerUserDataTypeEnum } from '../interfaces/ProductMeshUserData';

export default class BimProduct {
    data: IProduct;
    group: THREE.Group = new THREE.Group();
    representationGroup: THREE.Group = new THREE.Group();
    #childrenGroup: THREE.Group = new THREE.Group();
    #children: BimProduct[] = [];
    parent: BimProduct | undefined;
    representations: ProductRepresentation[] = [];
    #fbxRepresentationCache = new Map<Representation3d, ProductRepresentation>();

    constructor(data: IProduct, parent: BimProduct | undefined) {
        this.data = data;
        
        this.representationGroup.name = "Representations";
        this.#childrenGroup.name = "Children";
        this.group.add(this.representationGroup);
        this.group.add(this.#childrenGroup);

        this.group.name = buildProductName(this.data);

        const userData : ProductMeshUserData = {
            userDataType: VisualizerUserDataTypeEnum.productMesh,
            product: this.data as Product
        }
         
        this.group.userData = userData;

        // Initial placement
        this.group.position.copy(this.data.placement.location);
        this.group.quaternion.copy(this.data.placement.getRotationQuaternion());

        this.parent = parent;
    }

    addChild(child: BimProduct) {
        this.#children.push(child);
        this.#childrenGroup.add(child.group);
    }

    select3dRepresentations(representations: Representation3d[]) : Representation3d[] {
        const fbxRepresentation = representations.find(r => 
                                    r.representationType == Representation3dTypeEnum.FbxAsset);

        if (fbxRepresentation) {
            return [fbxRepresentation];
        }

        const triangleMeshRepresentation = representations.find(r => 
                                    r.representationType == Representation3dTypeEnum.TriangleMesh);

        if (triangleMeshRepresentation) {
            return [triangleMeshRepresentation];
        }

        // Return "the rest" which is likely ExtrudedAreaSolid for now
        return representations;
    };

    updateRepresentations( materialPairs?: Record<number, MaterialPair>, 
                            materialSet?: IMaterialSet,
                            stageType?: StageTypeEnum) {
        // Update self
        this.representationGroup.clear();

        // Collect old non-FBX representations for disposal after the new ones are built.
        // FBX representations in the cache are intentionally kept alive.
        const fbxCacheValues = new Set(this.#fbxRepresentationCache.values());
        const toDispose = this.representations.filter(r => !fbxCacheValues.has(r));

        // Combine representations into a single array
        const allRepresentations = [    ...this.data.representations2d, 
                                        ...this.select3dRepresentations(this.data.representations3d)];
        
        const nextRepresentations: ProductRepresentation[] = [];

        for (const representation of allRepresentations) {
            const isFbx = representation instanceof Representation3d &&
                          representation.representationType === Representation3dTypeEnum.FbxAsset;

            if (isFbx) {
                const cached = this.#fbxRepresentationCache.get(representation);
                if (cached) {
                    // Reuse existing FBX representation — no reload needed
                    this.representationGroup.add(cached.geometry);
                    nextRepresentations.push(cached);
                    continue;
                }

                if (stageType !== StageTypeEnum.Stage3d) {
                    // Not in 3D mode yet — defer FBX loading until first 3D entry
                    continue;
                }
            }

            const productRepr = createProductRepresentation(
                representation,
                this.data,
                materialPairs,
                materialSet
            );
            if (!productRepr) continue;

            if (isFbx) {
                this.#fbxRepresentationCache.set(representation, productRepr);
            }

            this.representationGroup.add(productRepr.geometry);
            nextRepresentations.push(productRepr);
        }

        // Dispose old non-FBX representations now that new ones are in place
        toDispose.forEach(r => r.dispose());

        this.representations = nextRepresentations;

        // Update children
        this.#children.forEach(child => child.updateRepresentations(materialPairs, materialSet, stageType));
    }

    onRotationOffsetChanged(rotationOffset: number) {

        // Notify children
        this.#children.forEach(child => child.onRotationOffsetChanged(rotationOffset) );
    }

    dispose() {
        this.representations.forEach(r => r.dispose());
        this.#fbxRepresentationCache.forEach(r => r.dispose());
        this.#fbxRepresentationCache.clear();
        this.#children.forEach(child => child.dispose());
    }
}
